import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TYPES, TYPE_LABEL, fmtDays } from "@/components/leave/shared";

export const HrBalances = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/leave/balances").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const shown = rows.filter((r) => `${r.employee.name} ${r.employee.employee_code || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="hr-balances">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee" className="rounded-none h-9 text-xs w-60 mb-3" data-testid="hr-bal-search" />
      <div className="border border-[#E5E7EB] overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
            <tr>{["Employee", "Emp ID", "Joining", ...TYPES.map((t) => TYPE_LABEL[t]), ""].map((h) => <th key={h} className="text-left px-3 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map(({ employee: e, balances: b }) => (
              <tr key={e.id} className="border-b border-[#F3F4F6]" data-testid={`hr-bal-row-${e.id}`}>
                <td className="px-3 py-2 font-bold">{e.name}</td>
                <td className="px-3 py-2 text-xs">{e.employee_code || "—"}</td>
                <td className="px-3 py-2 text-xs">{e.joining_date ? fmtDate(e.joining_date) : <span className="text-[#B45309]">Not set</span>}</td>
                {TYPES.map((t) => (
                  <td key={t} className="px-3 py-2"><b>{fmtDays(b[t].available)}</b> <span className="text-[11px] text-[#9CA3AF]">/ {fmtDays(b[t].total)}</span></td>
                ))}
                <td className="px-3 py-2"><Button variant="outline" className="rounded-none h-8 text-[10px] uppercase tracking-widest font-bold" onClick={() => setOpen(e.id)} data-testid={`hr-bal-manage-${e.id}`}>Manage</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <LedgerDialog eid={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
};

const LedgerDialog = ({ eid, onClose }) => {
  const [d, setD] = useState(null);
  const [adj, setAdj] = useState({ leave_type: "CL", days: "", reason: "", date: "" });
  const load = () => api.get(`/leave/employees/${eid}/ledger`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [eid]); // eslint-disable-line
  const save = async () => {
    try {
      await api.post("/leave/adjustments", { ...adj, employee_id: eid, days: Number(adj.days), date: adj.date || undefined });
      toast.success("Adjustment recorded");
      setAdj({ leave_type: adj.leave_type, days: "", reason: "", date: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hr-ledger-dialog">
        <DialogHeader><DialogTitle className="font-black tracking-tight">{d?.employee?.name || "…"} — Leave Ledger</DialogTitle></DialogHeader>
        {d && (<>
          <div className="grid grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <div key={t} className="flat-card p-3" data-testid={`ledger-bal-${t}`}>
                <div className="label-eyebrow">{TYPE_LABEL[t]}</div>
                <div className="text-2xl font-black">{fmtDays(d.balances[t].available)}</div>
                <div className="text-[10px] text-[#4B5563]">Credited {fmtDays(d.balances[t].credited)} · Carry {fmtDays(d.balances[t].carry_forward)} · Adj {fmtDays(d.balances[t].adjustments)} · Taken {fmtDays(d.balances[t].taken)}</div>
              </div>
            ))}
          </div>
          <div className="flat-card p-4 mt-3">
            <div className="label-eyebrow mb-2">Manual Adjustment (+ credit / − deduct)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Select value={adj.leave_type} onValueChange={(v) => setAdj({ ...adj, leave_type: v })}>
                <SelectTrigger className="rounded-none h-9 text-xs" data-testid="adj-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" step="0.5" value={adj.days} onChange={(e) => setAdj({ ...adj, days: e.target.value })} placeholder="Days e.g. 2 or -1" className="rounded-none h-9 text-xs" data-testid="adj-days" />
              <Input type="date" value={adj.date} onChange={(e) => setAdj({ ...adj, date: e.target.value })} className="rounded-none h-9 text-xs" data-testid="adj-date" />
              <Button onClick={save} className="rounded-none h-9 bg-[#002FA7] text-xs uppercase tracking-widest font-bold" data-testid="adj-save">Record</Button>
            </div>
            <Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Reason / remarks (required)" className="rounded-none h-9 text-xs mt-2" data-testid="adj-reason" />
          </div>
          <Section title="Adjustment History" testid="ledger-adjustments" rows={d.adjustments} empty="No manual adjustments."
            render={(a) => <><b>{a.leave_type} {a.days > 0 ? "+" : ""}{fmtDays(a.days)}</b> · {fmtDate(a.date)} · {a.reason} <span className="text-[#9CA3AF]">— {a.adjusted_by}</span></>} />
          <Section title="Earned Leave Credit History (automatic)" testid="ledger-el-credits" rows={d.el_credits}
            empty={d.employee.joining_date ? "Not yet eligible." : "Set the joining date to calculate Earned Leave."}
            render={(c) => <><b>+{fmtDays(c.days)}</b> · {fmtDate(c.date)} · {c.note}</>} />
          {d.el_next && <div className="text-xs text-[#4B5563] mt-2" data-testid="ledger-el-next">Next EL credit: {fmtDays(d.el_next.days)} day(s) on {fmtDate(d.el_next.date)}</div>}
        </>)}
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, rows, render, empty, testid }) => (
  <div className="mt-4" data-testid={testid}>
    <div className="label-eyebrow mb-2">{title} ({rows.length})</div>
    {rows.length === 0 ? <div className="text-xs text-[#9CA3AF]">{empty}</div> : (
      <div className="max-h-52 overflow-y-auto divide-y divide-[#F3F4F6] border border-[#E5E7EB]">
        {rows.map((r, i) => <div key={r.id || i} className="px-3 py-1.5 text-xs">{render(r)}</div>)}
      </div>
    )}
  </div>
);

